import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users,
  Plus,
  Pencil,
  Trash2,
  Shield,
  UserCog,
  Eye,
  Mail,
  Calendar,
  Building2,
  Megaphone,
  DollarSign,
  KeyRound
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUNCTIONS = [
  { value: 'brokerage', label: 'Brokerage', icon: Building2 },
  { value: 'mga', label: 'MGA', icon: Shield },
  { value: 'marketing', label: 'Marketing', icon: Megaphone },
  { value: 'finance', label: 'Finance', icon: DollarSign },
  { value: 'people', label: 'People', icon: Users },
];

const UserManagement = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'elt',
    functions: []
  });

  const roles = [
    { value: 'admin', label: 'Admin', icon: Shield, description: 'Full access to all features' },
    { value: 'elt', label: 'ELT', icon: UserCog, description: 'Can view and edit KPI data' },
    { value: 'board', label: 'Board', icon: Eye, description: 'Read-only access to board view' },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    // Generate a random password for new users
    const generatePassword = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
      let password = 'PH';
      for (let i = 0; i < 8; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password + '!';
    };
    
    setFormData({
      email: '',
      name: '',
      password: generatePassword(),
      role: 'elt',
      functions: []
    });
  };

  const handleAdd = async () => {
    if (!formData.email || !formData.name || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/users`, formData);
      toast.success('User created successfully');
      setIsAddDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = async () => {
    if (!formData.email || !formData.name) {
      toast.error('Email and name are required');
      return;
    }
    
    try {
      const updates = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        functions: formData.functions
      };
      
      if (formData.password) {
        updates.password = formData.password;
      }
      
      await axios.put(`${API}/users/${selectedUser.id}`, updates);
      toast.success('User updated successfully');
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResendInvite = async (user) => {
    try {
      await axios.post(`${API}/users/${user.id}/resend-invite`);
      toast.success(`Invite sent to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setSendPasswordEmail(true);
    setIsResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const response = await axios.post(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
        send_email: sendPasswordEmail
      });
      
      toast.success(`Password reset for ${selectedUser.name}`);
      if (response.data.notification_sent) {
        toast.success(`Email notification sent to ${selectedUser.email}`);
      }
      setIsResetPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      functions: user.functions || []
    });
    setIsEditDialogOpen(true);
  };

  const toggleFunction = (func) => {
    setFormData(prev => ({
      ...prev,
      functions: prev.functions.includes(func)
        ? prev.functions.filter(f => f !== func)
        : [...prev.functions, func]
    }));
  };

  const getRoleIcon = (role) => {
    const r = roles.find(ro => ro.value === role);
    return r ? r.icon : Users;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-rose-100 text-rose-700';
      case 'elt':
        return 'bg-blue-100 text-blue-700';
      case 'board':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    elt: users.filter(u => u.role === 'elt').length,
    board: users.filter(u => u.role === 'board').length
  };

  if (!isAdmin()) {
    return (
      <Layout>
        <Header title="User Management" subtitle="Admin access required" />
        <div className="p-6 text-center text-slate-500">
          You don't have permission to access this page.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="User Management" subtitle="Manage user accounts and permissions" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="user-management">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-500">Total Users</span>
              <Users className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-2">{stats.total}</p>
          </div>
          <div className="bg-rose-50 rounded-lg border border-rose-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-rose-700">Admins</span>
              <Shield className="w-5 h-5 text-rose-600" />
            </div>
            <p className="text-2xl font-bold text-rose-800 mt-2">{stats.admins}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700">ELT</span>
              <UserCog className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-800 mt-2">{stats.elt}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-emerald-700">Board</span>
              <Eye className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-800 mt-2">{stats.board}</p>
          </div>
        </div>

        {/* Add User Button */}
        <div className="flex justify-end">
          <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} className="bg-[#2563EB]">
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Assigned Functions</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const RoleIcon = getRoleIcon(user.role);
                  const isCurrentUser = user.id === currentUser?.id;
                  const userFunctions = user.functions || [];
                  
                  return (
                    <TableRow key={user.id} className={cn(isCurrentUser && 'bg-blue-50/50')}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium">{user.name}</span>
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-blue-600">(You)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize",
                          getRoleBadgeColor(user.role)
                        )}>
                          <RoleIcon className="w-3 h-3" />
                          {user.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        {user.role === 'admin' ? (
                          <span className="text-xs text-slate-400 italic">All (Admin)</span>
                        ) : user.role === 'board' ? (
                          <span className="text-xs text-slate-400 italic">N/A</span>
                        ) : userFunctions.length === 0 ? (
                          <span className="text-xs text-amber-600">All (No assignment)</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {userFunctions.map(func => (
                              <span key={func} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700 capitalize">
                                {func}
                              </span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <Calendar className="w-4 h-4" />
                          {new Date(user.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => openEditDialog(user)}
                            title="Edit user"
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => openResetPasswordDialog(user)}
                            title="Reset password"
                          >
                            <KeyRound className="w-4 h-4 text-amber-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleResendInvite(user)}
                            title="Resend invite email"
                          >
                            <Mail className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => { setSelectedUser(user); setIsDeleteDialogOpen(true); }}
                            disabled={isCurrentUser}
                            title="Delete user"
                          >
                            <Trash2 className={cn(
                              "w-4 h-4",
                              isCurrentUser ? "text-slate-300" : "text-rose-500"
                            )} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Role Descriptions */}
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {roles.map((role) => {
              const Icon = role.icon;
              return (
                <div key={role.value} className="flex items-start gap-3">
                  <div className={cn("p-2 rounded-lg", getRoleBadgeColor(role.value))}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-700">{role.label}</p>
                    <p className="text-sm text-slate-500">{role.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(formData.password);
                    toast.success('Password copied!');
                  }}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Auto-generated password. The user will receive this in their welcome email.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v, functions: [] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex items-center gap-2">
                        <r.icon className="w-4 h-4" />
                        {r.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role === 'elt' && (
              <div className="space-y-2">
                <Label>Assigned Functions</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Select which departments this user will receive notifications for. Leave empty for all.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {FUNCTIONS.map(func => (
                    <div 
                      key={func.value}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors",
                        formData.functions.includes(func.value) 
                          ? "bg-blue-50 border-blue-300" 
                          : "bg-white border-slate-200 hover:border-slate-300"
                      )}
                      onClick={() => toggleFunction(func.value)}
                    >
                      <Checkbox 
                        checked={formData.functions.includes(func.value)}
                        className="pointer-events-none"
                      />
                      <func.icon className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{func.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-[#2563EB]">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details for {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>New Password (leave blank to keep current)</Label>
              <Input
                type="password"
                placeholder="Enter new password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v, functions: v === 'elt' ? formData.functions : [] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role === 'elt' && (
              <div className="space-y-2">
                <Label>Assigned Functions</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Select which departments this user will receive notifications for. Leave empty for all.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {FUNCTIONS.map(func => (
                    <div 
                      key={func.value}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors",
                        formData.functions.includes(func.value) 
                          ? "bg-blue-50 border-blue-300" 
                          : "bg-white border-slate-200 hover:border-slate-300"
                      )}
                      onClick={() => toggleFunction(func.value)}
                    >
                      <Checkbox 
                        checked={formData.functions.includes(func.value)}
                        className="pointer-events-none"
                      />
                      <func.icon className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{func.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#2563EB]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-500" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{selectedUser?.name}</strong> ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                Tip: Use a simple temporary password like "Welcome123!" that the user can change later.
              </p>
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="sendEmail"
                checked={sendPasswordEmail}
                onCheckedChange={setSendPasswordEmail}
              />
              <label htmlFor="sendEmail" className="text-sm cursor-pointer">
                Send email notification with new password
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleResetPassword} 
              className="bg-amber-500 hover:bg-amber-600"
              disabled={resettingPassword || !newPassword || newPassword.length < 6}
            >
              {resettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default UserManagement;
